import {
  FINANCING_LABELS,
  SEPARATION_OUTCOME_LABELS,
  type SeparationGroupSummary,
  type SeparationRow,
  type SeparationsReport,
  shiftMonthKey,
} from '@performance-crm/shared';
import { Download, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { currentMonth } from '@/lib/months';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast-context';
import { CURRENCY_SYMBOL, fmt, todayInTz } from '@/lib/format';
import { useAdvisors, useSeparationsReport } from '@/lib/queries';
import { downloadWorkbook, excelDate, type SheetCell } from '@/lib/spreadsheet';
import { useCompanyTimezone } from '@/lib/useCompanyTimezone';

const OUTCOME_TONE: Record<SeparationRow['outcome'], 'success' | 'warning' | 'danger' | 'muted'> = {
  VENDIDA: 'success',
  VIGENTE: 'warning',
  CAIDA: 'danger',
  ANULADA: 'muted',
  EXCLUIDA: 'muted',
};

/** Por defecto: desde el primer día de hace 11 meses (12 meses móviles) en la zona de la empresa. */
const defaultFrom = (tz: string) => `${shiftMonthKey(currentMonth(tz), -11)}-01`;

const pct = (v: number | null) => (v === null ? '—' : `${(v * 100).toFixed(1)}%`);
const excelPct = (v: number | null): SheetCell => (v === null ? null : Math.round(v * 1000) / 10);

function GroupTable({
  title,
  description,
  rows,
  firstColumn,
  testId,
}: {
  title: string;
  description?: string;
  rows: SeparationGroupSummary[];
  firstColumn: string;
  testId?: string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid={testId}>
          <thead className="bg-muted/50 text-muted-foreground text-left text-xs uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2">{firstColumn}</th>
              <th className="px-3 py-2 text-right">Separaciones</th>
              <th className="px-3 py-2 text-right">Vendidas</th>
              <th className="px-3 py-2 text-right">Caídas</th>
              <th className="px-3 py-2 text-right">Vigentes</th>
              <th className="px-3 py-2 text-right">% caídas</th>
              <th className="px-3 py-2 text-right">% cierre</th>
              <th className="px-3 py-2 text-right">Días a venta</th>
              <th className="px-3 py-2 text-right">Días a caída</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((g) => (
              <tr key={g.key} className="border-t">
                <td className="px-3 py-2 font-medium">{g.label}</td>
                <td className="px-3 py-2 text-right">{g.separations}</td>
                <td className="px-3 py-2 text-right">{g.sold}</td>
                <td className="text-danger px-3 py-2 text-right">{g.dropped}</td>
                <td className="px-3 py-2 text-right">{g.pending}</td>
                <td className="px-3 py-2 text-right font-medium">{pct(g.dropRate)}</td>
                <td className="px-3 py-2 text-right font-medium">{pct(g.closeRate)}</td>
                <td className="text-muted-foreground px-3 py-2 text-right">
                  {g.avgDaysToSale ?? '—'}
                </td>
                <td className="text-muted-foreground px-3 py-2 text-right">
                  {g.avgDaysToDrop ?? '—'}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={9} className="text-muted-foreground px-3 py-6 text-center">
                  Sin separaciones en el periodo.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function buildSheets(data: SeparationsReport, tz: string) {
  const groupHeader = (first: string) => [
    first,
    'Separaciones',
    'Vendidas',
    'Caídas',
    'Vigentes',
    '% caídas',
    '% cierre',
    'Anuladas',
    'Excluidas',
    'Días prom. a venta',
    'Días prom. a caída',
  ];
  const groupRows = (gs: SeparationGroupSummary[]): SheetCell[][] =>
    gs.map((g) => [
      g.label,
      g.separations,
      g.sold,
      g.dropped,
      g.pending,
      excelPct(g.dropRate),
      excelPct(g.closeRate),
      g.annulled,
      g.excluded,
      g.avgDaysToSale,
      g.avgDaysToDrop,
    ]);
  return [
    {
      name: 'Separaciones',
      header: [
        'Fecha separación',
        'Resultado',
        'Cliente',
        'Proyecto',
        'Unidades',
        'Asesor',
        'Medio original',
        'Categoría',
        'Inicial',
        'Moneda de la reserva',
        'Monto total',
        'Moneda del precio',
        'Modalidad',
        'Banco',
        'Fecha venta',
        'Fecha caída',
        'Motivo de caída',
        'Notas',
        'Días hasta el resultado',
        'Motivo de anulación',
        'Motivo de exclusión',
        'ID lead',
      ],
      rows: data.rows.map<SheetCell[]>((r) => [
        excelDate(r.startedAt, tz),
        SEPARATION_OUTCOME_LABELS[r.outcome],
        r.contactName,
        r.projectName,
        r.units.join(', '),
        r.advisorName ?? 'Sin asesor',
        r.originalMedium,
        r.originalMediumCategory,
        r.amountInitial === null ? null : Number(r.amountInitial),
        CURRENCY_SYMBOL[r.initialCurrency] ?? r.initialCurrency,
        r.amountTotal === null ? null : Number(r.amountTotal),
        CURRENCY_SYMBOL[r.currency] ?? r.currency,
        r.contractMode ? FINANCING_LABELS[r.contractMode as keyof typeof FINANCING_LABELS] : null,
        r.bank,
        excelDate(r.soldAt, tz),
        excelDate(r.refundedAt, tz),
        r.dropReasonLabel,
        r.refundNotes,
        r.daysToOutcome,
        r.annulReason,
        r.excludedReason,
        r.leadId,
      ]),
    },
    {
      name: 'Por asesor',
      header: groupHeader('Asesor'),
      rows: [...groupRows(data.summary.byAdvisor), ...groupRows([data.summary.total])],
    },
    {
      name: 'Por proyecto',
      header: groupHeader('Proyecto'),
      rows: groupRows(data.summary.byProject),
    },
    { name: 'Por mes', header: groupHeader('Mes'), rows: groupRows(data.summary.byMonth) },
    {
      name: 'Motivos de caída',
      header: ['Motivo', 'Caídas', '% de las caídas'],
      rows: data.summary.byDropReason.map<SheetCell[]>((r) => [r.label, r.count, excelPct(r.pct)]),
    },
  ];
}

/** Caídas y cierre: historial de separaciones del periodo con su resultado, % de caídas por asesor y export a Excel. */
export function SeparationsTab({
  companyId,
  projectId,
}: {
  companyId: string;
  projectId?: string;
}) {
  const tz = useCompanyTimezone(companyId);
  const [from, setFrom] = useState(() => defaultFrom(tz));
  const [to, setTo] = useState('');
  const [advisorId, setAdvisorId] = useState('');
  const [exporting, setExporting] = useState(false);
  const toast = useToast();
  const { data: advisors } = useAdvisors(companyId);
  // Las fechas viajan tal cual (AAAA-MM-DD): la API las resuelve como días completos de la zona de la empresa,
  // con el «hasta» inclusivo (ux-comercial-13), igual que el embudo.
  const { data, isLoading } = useSeparationsReport({
    companyId,
    projectId,
    advisorId: advisorId || undefined,
    from: from || undefined,
    to: to || undefined,
  });
  const exportExcel = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const stamp = `${from || 'inicio'}_a_${to || todayInTz(tz)}`;
      await downloadWorkbook(buildSheets(data, tz), `separaciones-${stamp}.xlsx`);
    } catch (err) {
      toast.error('No se pudo generar el Excel', (err as Error).message);
    } finally {
      setExporting(false);
    }
  };
  const t = data?.summary.total;
  return (
    <div className="space-y-4">
      <div className="bg-card flex flex-wrap items-center gap-2 rounded-lg border p-3">
        <Input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          aria-label="Desde"
          className="h-9 w-40"
        />
        <span className="text-muted-foreground text-sm">a</span>
        <Input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          aria-label="Hasta"
          className="h-9 w-40"
        />
        <Select
          value={advisorId}
          onChange={(e) => setAdvisorId(e.target.value)}
          aria-label="Asesor"
          className="h-9 w-52"
        >
          <option value="">Todos los asesores</option>
          {(advisors ?? [])
            .filter((a) => a.kind === 'HUMAN')
            .map((a) => (
              <option key={a.id} value={a.id}>
                {a.fullName}
              </option>
            ))}
        </Select>
        <Button
          variant="outline"
          size="sm"
          className="ml-auto"
          onClick={() => void exportExcel()}
          disabled={!data || exporting}
          data-testid="export-separations"
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}{' '}
          Exportar Excel
        </Button>
      </div>
      <p className="text-muted-foreground text-xs">
        Cada separación del periodo (por fecha de separación) con su resultado a hoy: <b>vendida</b>{' '}
        (llegó a venta), <b>caída</b> (devolución) o <b>vigente</b> (aún sin resolver). Las anuladas
        y las excluidas (motivo de caída técnico o sin unidad contable, p. ej. solo un
        estacionamiento) se listan pero no entran en los porcentajes, igual que en el embudo. %
        caídas = caídas ÷ separaciones; % cierre = vendidas ÷ separaciones.
      </p>
      {isLoading && <Skeleton className="h-64 w-full" />}
      {data && t && (
        <>
          {data.truncated && (
            <p className="text-warning text-sm">
              Se muestran las primeras {fmt.number(data.rows.length)} separaciones: acota el periodo
              para ver el historial completo.
            </p>
          )}
          <div
            className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6"
            data-testid="separations-summary"
          >
            {(
              [
                ['Separaciones', String(t.separations)],
                ['Vendidas', String(t.sold)],
                ['Caídas', String(t.dropped)],
                ['Vigentes', String(t.pending)],
                ['% caídas', pct(t.dropRate)],
                ['% cierre', pct(t.closeRate)],
              ] as const
            ).map(([label, v]) => (
              <Card key={label}>
                <CardContent className="p-4">
                  <p className="text-muted-foreground text-xs uppercase">{label}</p>
                  <p className="text-2xl font-semibold">{v}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <GroupTable
            title="Por asesor"
            description="Efectividad para cerrar: de cada separación, cuántas llegan a venta y cuántas se caen."
            rows={data.summary.byAdvisor}
            firstColumn="Asesor"
            testId="separations-by-advisor"
          />
          <div className="grid gap-4 lg:grid-cols-2">
            <GroupTable
              title="Por mes de separación"
              rows={data.summary.byMonth}
              firstColumn="Mes"
            />
            <div className="space-y-4">
              <GroupTable
                title="Por proyecto"
                rows={data.summary.byProject}
                firstColumn="Proyecto"
              />
              <Card>
                <CardHeader>
                  <CardTitle>Motivos de caída</CardTitle>
                </CardHeader>
                <CardContent>
                  {data.summary.byDropReason.length === 0 && (
                    <p className="text-muted-foreground text-sm">Sin caídas en el periodo.</p>
                  )}
                  <ul className="space-y-1.5 text-sm">
                    {data.summary.byDropReason.map((r) => (
                      <li key={r.key}>
                        <div className="flex justify-between">
                          <span>{r.label}</span>
                          <span className="text-muted-foreground">
                            {r.count} · {fmt.pct(r.pct)}
                          </span>
                        </div>
                        <div className="bg-muted mt-0.5 h-1.5 rounded">
                          <div
                            className="bg-danger h-1.5 rounded"
                            style={{ width: `${Math.round(r.pct * 100)}%` }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Historial ({fmt.number(data.rows.length)})</CardTitle>
              <CardDescription>
                Las mismas filas que el Excel; se muestran hasta 300 en pantalla.
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground text-left text-xs uppercase tracking-wide">
                  <tr>
                    <th className="px-3 py-2">Fecha</th>
                    <th className="px-3 py-2">Resultado</th>
                    <th className="px-3 py-2">Cliente</th>
                    <th className="px-3 py-2">Proyecto</th>
                    <th className="px-3 py-2">Unidades</th>
                    <th className="px-3 py-2">Asesor</th>
                    <th className="px-3 py-2">Medio original</th>
                    <th className="px-3 py-2 text-right">Inicial</th>
                    <th className="px-3 py-2">Cierre / caída</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.slice(0, 300).map((r) => (
                    <tr key={r.id} className="border-t" data-testid="separation-row">
                      <td className="whitespace-nowrap px-3 py-2">{fmt.date(r.startedAt)}</td>
                      <td className="px-3 py-2">
                        <Badge tone={OUTCOME_TONE[r.outcome]}>
                          {SEPARATION_OUTCOME_LABELS[r.outcome]}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <Link to={`/leads/${r.leadId}`} className="text-primary hover:underline">
                          {r.contactName}
                        </Link>
                      </td>
                      <td className="px-3 py-2">{r.projectName}</td>
                      <td className="px-3 py-2">{r.units.join(', ') || '—'}</td>
                      <td className="px-3 py-2">{r.advisorName ?? 'Sin asesor'}</td>
                      <td className="px-3 py-2">{r.originalMedium ?? '—'}</td>
                      <td className="px-3 py-2 text-right">
                        {fmt.money(r.amountInitial, r.initialCurrency)}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-xs">
                        {r.outcome === 'VENDIDA' &&
                          `Venta ${fmt.date(r.soldAt)} · ${r.daysToOutcome} d`}
                        {r.outcome === 'CAIDA' &&
                          `${fmt.date(r.refundedAt)} · ${r.dropReasonLabel ?? r.dropReason ?? ''} · ${r.daysToOutcome} d`}
                        {r.outcome === 'EXCLUIDA' && (r.excludedReason ?? '')}
                        {r.outcome === 'ANULADA' && (r.annulReason ?? '')}
                        {r.outcome === 'VIGENTE' && '—'}
                      </td>
                    </tr>
                  ))}
                  {data.rows.length === 0 && (
                    <tr>
                      <td colSpan={9} className="text-muted-foreground px-3 py-8 text-center">
                        Sin separaciones en el periodo.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
